import { MouseEvent } from "react";
import useControlled from "../useControlled";
import { useIsMobileDevice } from "../useIsMobileDevice.hook";
import {
  BasePaginationProps,
  PaginationItemType,
  UsePaginationResult,
} from "./pagination.type";

export default function usePagination(
  props: BasePaginationProps = {}
): UsePaginationResult {
  const isMobileDevice = useIsMobileDevice();
  const {
    componentName = "usePagination",
    count = 1,
    defaultPage = 1,
    disabled = false,
    onChange: handleChange,
    page: pageProp,
    ...other
  } = props;

  const [page, setPageState] = useControlled({
    controlled: pageProp,
    default: defaultPage,
    name: componentName,
    state: "page",
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>, value: number) => {
    if (!pageProp) {
      setPageState(value);
    }
    if (handleChange) {
      handleChange(event, value);
    }
  };

  // https://dev.to/namirsab/comment/2050
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, page <= 3 ? (isMobileDevice ? 1 : 3) : 1);

  const noOfStartPages = isMobileDevice ? 1 : 3;

  const endPages =
    page > count - noOfStartPages
      ? range(count - noOfStartPages + 1, count)
      : [count];
  // Basic list of items to render
  // e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']
  let itemList = ["first", "prev"] as (PaginationItemType | number)[];
  if (isMobileDevice && count <= 3) {
    itemList = [...itemList, ...range(1, count)];
  } else if (!isMobileDevice && count <= 5) {
    itemList = [...itemList, ...range(1, count)];
  } else {
    itemList = [
      ...itemList,

      ...(page <= noOfStartPages ? startPages : []),

      ...(startPages[startPages.length - 1] - page < 0 && !isMobileDevice
        ? ["start-ellipsis"]
        : []),
      ...(page > noOfStartPages && page < endPages[0]
        ? isMobileDevice
          ? [page]
          : [page - 1, page, page + 1]
        : []),
      ...(endPages[0] - page > 0 && !isMobileDevice ? ["end-ellipsis"] : []),

      ...(endPages[0] - page <= 0 ? endPages : []),
    ];
  }

  itemList = [...itemList, "next", "last"] as PaginationItemType[];

  // Map the button type to its page number
  const buttonPage = (type: PaginationItemType) => {
    switch (type) {
      case "first":
        return 1;
      case "prev":
        return page - 1;
      case "next":
        return page + 1;
      case "last":
        return count;
      case "start-ellipsis":
        return page - 3;
      case "end-ellipsis":
        return page + 3;
      default:
        return null;
    }
  };

  // Convert the basic item list to PaginationItem props objects
  const items = itemList.map((item: PaginationItemType | number) => {
    return typeof item === "number"
      ? {
          onClick: (event: MouseEvent<HTMLButtonElement>) => {
            handleClick(event, item);
          },
          type: "page" as PaginationItemType,
          page: item,
          selected: item === page,
          disabled,
          "aria-current": item === page ? true : undefined,
        }
      : {
          onClick: (event: MouseEvent<HTMLButtonElement>) => {
            handleClick(event, buttonPage(item));
          },
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            disabled ||
            (item.indexOf("ellipsis") === -1 &&
              (item === "next" || item === "last" ? page >= count : page <= 1)),
        };
  });

  return {
    items,
    ...other,
  };
}
